import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper, CircularProgress
} from '@mui/material';
import { Payments } from '@mui/icons-material';
import { API_BASE_URL, FACTURACION_GET, PAGO_LICENCIA_SUBIR } from '../services/apiConfig';
import { avisar } from '../services/toast';

const fmtMoneda = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PagoLicenciaForm() {
  const [precioPorSucursal, setPrecioPorSucursal] = useState(0);
  const [cantidadSucursales, setCantidadSucursales] = useState(0);
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().split('T')[0]);
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const montoTotal = precioPorSucursal * cantidadSucursales;

  useEffect(() => {
    const fetchMonto = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(API_BASE_URL + FACTURACION_GET, { headers });
        if (response.data.success && response.data.data) {
          setPrecioPorSucursal(parseFloat(response.data.data.pago_mensual_monto) || 0);
          setCantidadSucursales(parseInt(response.data.data.cantidad_sucursales, 10) || 0);
        }
      } catch (error) {
        console.error('Error al obtener monto mensual:', error);
      }
    };
    fetchMonto();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!archivo) {
      avisar('Debe adjuntar un comprobante', 'warning');
      return;
    }
    if (!fechaPago) {
      avisar('Debe seleccionar una fecha', 'warning');
      return;
    }

    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append('comprobante', archivo);
      formData.append('fecha_pago', fechaPago);

      const token = sessionStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'multipart/form-data' };

      const response = await axios.post(API_BASE_URL + PAGO_LICENCIA_SUBIR, formData, { headers });

      if (response.data.status === 'success') {
        avisar('Comprobante enviado correctamente', 'success');
        setArchivo(null);
        document.getElementById('file-input').value = '';
      } else {
        avisar(response.data.message || 'Error al enviar', 'error');
      }
    } catch (error) {
      avisar('Error de conexión: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Payments color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6">Informar Pago de Licencia</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Adjunte el comprobante de pago mensual. Se enviará automáticamente por email.
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Precio por sucursal</Typography>
          <Typography variant="body2">{fmtMoneda(precioPorSucursal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">Sucursales activas</Typography>
          <Typography variant="body2">× {cantidadSucursales}</Typography>
        </Box>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 1, py: 1, mb: 1.5, borderRadius: 1, bgcolor: 'action.hover',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Total a pagar</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmtMoneda(montoTotal)}</Typography>
        </Box>
        <TextField
          label="Fecha de pago"
          type="date"
          fullWidth
          size="small"
          value={fechaPago}
          onChange={(e) => setFechaPago(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 1.5 }}
        />
        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{ mb: 1.5, textAlign: 'left', justifyContent: 'flex-start' }}
        >
          {archivo ? archivo.name : 'Seleccionar comprobante'}
          <input
            id="file-input"
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={(e) => setArchivo(e.target.files[0])}
          />
        </Button>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={enviando}
          startIcon={enviando ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {enviando ? 'Enviando...' : 'Enviar comprobante'}
        </Button>
      </Box>
    </Paper>
  );
}

export default PagoLicenciaForm;
